// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { createAssembly } from '@/assembly/api/create-assembly';
import { CreateAssemblyBomPart } from '@/assembly/components/create-assembly-bom-part';
import { assembliesQueryKey } from '@/assembly/hooks/use-assemblies';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormTextField } from '@/components/form-text-field';
import { SupportAlert } from '@/components/support-alert';
import { InventoryEntry, getCountAtLocation } from '@/inventory/api/types';
import { useInventory } from '@/inventory/hooks';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import { getItem } from '@/items/api/get-item';
import { ItemDetail } from '@/items/api/types';
import { ItemSearch } from '@/items/components/item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { toSiteId } from '@/location/utilities/to-site-id';
import {
  SparseItem,
  SparseLocation,
  sparseItemSchema,
  sparseLocationSchema,
} from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Local interface for the inventory at the selected location's site
 * The location is the selected site or zone, and each bulk item lists the site's locations
 */
interface LocationInventoryItem {
  location: SparseLocation;
  items: {
    bulkItems: {
      item: SparseItem;
      inventory: InventoryEntry[];
    }[];
  };
}

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;
}

const formSchema = z
  .object({
    productType: sparseItemSchema.nullable(),
    serialNumber: z.string().nullable(),
    assemblyLocation: sparseLocationSchema.nullable(),
    buildDate: dateSchema.nullable(),
    parts: z
      .array(
        z.object({
          part: sparseItemSchema,
          quantity: z
            .number()
            .min(1, {
              message: 'Quantity must be greater than 0',
            })
            .nullable(),
          serialNumbers: z.array(z.string()),
        })
      )
      .max(50),
    notes: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (!data.productType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Item type is required',
        path: ['productType'],
      });
    }

    if (!data.assemblyLocation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Assembly location is required',
        path: ['assemblyLocation'],
      });
    }

    if (!data.buildDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A build date is required',
        path: ['buildDate'],
      });
    } else if (
      data.buildDate instanceof DateTime &&
      data.buildDate > DateTime.now().plus({ days: 1 }).endOf('day') // Add 1 day to allow for timezone differences
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Build date cannot be in the future',
        path: ['buildDate'],
      });
    }

    if (!data.parts || data.parts.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'array',
        inclusive: true,
        message: 'At least one part is required',
        path: ['parts'],
      });
    } else {
      data.parts.forEach((item, index) => {
        if (!item.part) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Part is required',
            path: ['parts', index, 'part'],
          });
        }
        if (!item.quantity || item.quantity <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Quantity must be greater than 0',
            path: ['parts', index, 'quantity'],
          });
        }
      });
    }
  });

export type CreateAssemblyFormData = z.infer<typeof formSchema>;

const defaultValues: CreateAssemblyFormData = {
  productType: null,
  serialNumber: null,
  assemblyLocation: null,
  buildDate: null,
  parts: [],
  notes: null,
};

/**
 * Dialog for creating a new assembly.
 */
export function CreateAssemblyDialog(props: Props) {
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const [isLoadingBom, setIsLoadingBom] = useState(false);
  const queryClient = useQueryClient();

  const handleClose = () => {
    reset(defaultValues);
    setFormErrorMessage(null);
    props.onClose();
  };

  const createAssemblyMutation = useMutation({
    mutationFn: createAssembly,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...assembliesQueryKey],
      });
      invalidateInventory(queryClient);
      handleClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);

      // Check for invalid build date (future date)
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.assembly_invalid_build_date
      ) {
        setError('buildDate', {
          type: 'custom',
          message: 'Build date cannot be in the future',
        });
      } else {
        captureException(error);
        setFormErrorMessage('An error has occurred.');
      }
    },
  });

  const { control, reset, setError, handleSubmit, formState, watch } =
    useForm<CreateAssemblyFormData>({
      resolver: zodResolver(formSchema),
      defaultValues,
    });

  const {
    fields: partsField,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: 'parts',
  });

  const productType = watch('productType');
  const assemblyLocation = watch('assemblyLocation');
  const currentPartsValue = watch('parts');

  const selectedItemData = useRef<ItemDetail | null>(null);

  const selectedParts = useMemo(() => {
    return currentPartsValue
      .map((part) => part.part)
      .filter((part): part is NonNullable<typeof part> => part !== null);
  }, [currentPartsValue]);

  useEffect(() => {
    const fetchItemBom = async () => {
      if (productType) {
        setIsLoadingBom(true);
        try {
          const response = await getItem(productType.key);
          // Update the selected item
          selectedItemData.current = response.item;
          if (response.item.bom && response.item.bom.length > 0) {
            const formParts = response.item.bom.map((bomItem) => ({
              part: {
                id: bomItem.item.id,
                key: bomItem.item.key,
                name: bomItem.item.name,
                type: bomItem.item.type,
                sku: bomItem.item.sku,
                shouldRenderSerialFields: bomItem.item.shouldRenderSerialFields,
                isArchived: bomItem.item.isArchived,
                unitOfMeasure: bomItem.item.unitOfMeasure,
                hasAssets: bomItem.item.hasAssets,
                isPhysical: bomItem.item.isPhysical,
              },
              quantity: bomItem.quantity,
              serialNumbers: [],
            }));
            replace(formParts);
          } else {
            replace([]);
          }
        } catch (error) {
          captureException(error);
          setFormErrorMessage(
            'Error loading bill of materials, please try again.'
          );
        } finally {
          setIsLoadingBom(false);
        }
      } else {
        selectedItemData.current = null;
        replace([]);
      }
    };

    fetchItemBom();
  }, [productType, replace]);

  const assemblySiteId = assemblyLocation
    ? toSiteId(assemblyLocation)
    : undefined;
  const inventoryResult = useInventory({
    type: 'bulk-items',
    dimension: 'location',
    filters: {
      locationIds: assemblySiteId ? [assemblySiteId] : undefined,
    },
  });

  const inventoryData = useMemo((): LocationInventoryItem | undefined => {
    // When dimension is 'location', view is 'location' and inventory is InventoryByLocationResponse
    if (
      !inventoryResult.isLoading &&
      inventoryResult.view === 'location' &&
      inventoryResult.inventory?.data?.results &&
      assemblyLocation
    ) {
      const item = inventoryResult.inventory.data.results.find(
        (resultItem) => resultItem.location.id === assemblySiteId
      );

      if (item) {
        return {
          location: assemblyLocation,
          items: {
            bulkItems: item.items.bulkItems,
          },
        };
      }
      return {
        location: assemblyLocation,
        items: {
          bulkItems: [],
        },
      };
    }
    return undefined;
  }, [inventoryResult, assemblyLocation, assemblySiteId]);

  const parts = useWatch({
    control,
    name: 'parts',
  });

  const exceedsInventory =
    inventoryData &&
    parts &&
    parts.some((part) => {
      if (inventoryData === undefined) {
        // Not loaded
        return false;
      }
      const partInventoryItem = inventoryData.items.bulkItems.find(
        (p) => p.item.id === part.part.id
      );
      const availableQty = partInventoryItem
        ? getCountAtLocation(
            partInventoryItem.inventory,
            inventoryData.location.id
          )
        : 0;

      if (part.quantity) {
        return part.quantity > availableQty;
      }
      return false;
    });

  const onSubmit = async (data: CreateAssemblyFormData) => {
    setFormErrorMessage(null);

    if (!data.buildDate) {
      return;
    }

    return createAssemblyMutation.mutate({
      productId: data.productType?.id || '',
      serial: data.serialNumber,
      locationId: data.assemblyLocation?.id || '',
      buildDate: data.buildDate,
      parts: data.parts
        .filter((part) => part.part && part.quantity)
        .map((part) => ({
          partId: part.part?.id || '',
          quantity: part.quantity || 0,
          serials: part.serialNumbers,
        })),
      notes: data.notes,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={handleClose}
      scroll="paper"
    >
      <DialogTitle>Create assembly</DialogTitle>
      <DialogContent dividers>
        <form id="create-assembly-form" onSubmit={handleSubmit(onSubmit)}>
          {formErrorMessage && <SupportAlert message={formErrorMessage} />}
          <Grid container spacing={2}>
            {/* Item Type */}
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name="productType"
                render={({ field, fieldState }) => (
                  <ItemSearch
                    label="Inventory item"
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    filter={(items) => items.filter((item) => item.hasAssets)}
                  />
                )}
              />
            </Grid>

            {/* Serial Number */}
            <Grid item xs={12} md={6}>
              <FormTextField
                fullWidth
                name="serialNumber"
                label="Planned serial number"
                control={control}
              />
            </Grid>

            {/* Assembly Location */}
            <Grid item xs={12} md={6}>
              <FormLocationSearch
                name="assemblyLocation"
                label="Assembly location"
                control={control}
                onCreate={(name: string) => {
                  const url = getNewLocationURL(name);
                  window.open(url, '_blank');
                }}
              />
            </Grid>

            {/* Build Date */}
            <Grid item xs={12} md={6}>
              <FormDatePicker
                disableFuture
                name="buildDate"
                label="Build date"
                control={control}
              />
            </Grid>

            {/* Parts List */}
            <Grid item xs={12}>
              <Paper variant={'outlined'} sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">Parts list</Typography>
                  <Chip
                    size="small"
                    label={`${partsField.reduce((total, p) => total + (p.quantity ?? 0), 0)} parts (${partsField.length} SKUs)`}
                  />
                </Stack>

                <Stack gap={2}>
                  {isLoadingBom ? (
                    <Stack spacing={2}>
                      <Skeleton variant="rectangular" height={56} />
                    </Stack>
                  ) : productType === null ? (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Select a product to construct
                    </Typography>
                  ) : (
                    <>
                      <Stack direction="row" sx={{ width: '100%', pb: 2 }}>
                        <ItemSearch
                          type="BULK"
                          fullWidth
                          showSearchAdornment
                          value={null}
                          placeholderText="Select a part"
                          onChange={(item) => {
                            if (item) {
                              append({
                                part: item,
                                quantity: null,
                                serialNumbers: [],
                              });
                            }
                          }}
                          hiddenItems={selectedParts}
                        />
                      </Stack>
                      {partsField.length === 0 ? (
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          No parts added
                        </Typography>
                      ) : (
                        partsField.map((field, index) => (
                          <CreateAssemblyBomPart
                            key={field.id}
                            index={index}
                            control={control}
                            selectedParts={selectedParts}
                            selectedItem={selectedItemData.current}
                            inventoryData={inventoryData}
                            onRemove={() => remove(index)}
                            isDisableDelete={partsField.length <= 1}
                          />
                        ))
                      )}
                    </>
                  )}
                </Stack>
                {formState.errors.parts && (
                  <Typography color="error" variant="caption">
                    {formState.errors.parts.message}
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <FormTextField
                fullWidth
                isMultiline
                minimumRows={3}
                name="notes"
                label="Notes"
                placeholder="Optional notes about this assembly"
                control={control}
              />
            </Grid>
            {exceedsInventory && (
              <Alert
                severity={'warning'}
                sx={{ width: '95%', mx: 'auto', mt: 2 }}
              >
                Warning: Some parts exceed available inventory
              </Alert>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="submit"
          form="create-assembly-form"
          variant="contained"
          color="primary"
          disabled={createAssemblyMutation.isPending}
        >
          Create assembly
        </Button>
      </DialogActions>
    </Dialog>
  );
}
